/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */
import { useTranslationContext } from "@gnu-taler/web-util/browser";
import { ComponentChildren, h, VNode } from "preact";
import { useRef, useState } from "preact/hooks";
import { MAX_IMAGE_SIZE as MAX_IMAGE_UPLOAD_SIZE } from "../../utils/constants.js";
import { InputProps, useField } from "./useField.js";
import { Tooltip } from "../Tooltip.js";
import { TranslatedString } from "@gnu-taler/taler-util";

const TALER_SCREEN_ID = 9;

export interface Props<T> extends InputProps<T> {
  expand?: boolean;
  addonAfter?: ComponentChildren;
  children?: ComponentChildren;
}

export function InputImage<T>({
  name,
  readonly,
  placeholder,
  tooltip,
  label,
  help,
  children,
  expand,
}: Props<keyof T>): VNode {
  const { error, value, onChange } = useField<T>(name);

  const image = useRef<HTMLInputElement>(null);
  const { i18n } = useTranslationContext();
  const [sizeError, setSizeError] = useState<TranslatedString>();

  return (
    <div class="field is-horizontal">
      <div class="field-label is-normal">
        <label class="label">
          {label}
          {tooltip && (
            <Tooltip text={tooltip}>
              <i class="icon mdi mdi-information" />
            </Tooltip>
          )}
        </label>
      </div>
      <div class="field-body is-flex-grow-3">
        <div class="field">
          <p class={expand ? "control is-expanded" : "control"}>
            {value && (
              <img
                src={value}
                style={{ width: 200, height: 200 }}
                onClick={() => image.current?.click()}
              />
            )}
            <input
              ref={image}
              style={{ display: "none" }}
              type="file"
              name={String(name)}
              placeholder={placeholder}
              readonly={readonly}
              onChange={(e) => {
                const fileList: FileList | null = e.currentTarget.files;
                if (!fileList || fileList.length != 1) {
                  return onChange(undefined!);
                }
                const theFile = fileList[0];
                if (!theFile.type.startsWith("image/")) {
                  setSizeError(i18n.str`The file is not an image`);
                  return;
                }
                if (theFile.size > MAX_IMAGE_UPLOAD_SIZE) {
                  setSizeError(i18n.str`The image was normalized to be smaller than 1 MB`);
                  normalizeImagesize(theFile, onChange as any);
                  // return onChange(undefined!);
                  return;
                }
                setSizeError(undefined);
                toDataSrc(theFile).then(r => {
                  onChange(r as T[keyof T]);
                })
              }}
            />
            {help}
            {children}
          </p>
          {error && (
            <p class="help is-danger" style={{ fontSize: 16 }}>
              {error}
            </p>
          )}
          {sizeError && (
            <p class="help is-danger" style={{ fontSize: 16 }}>
              {sizeError}
            </p>
          )}
          {!value && (
            <button
              class="button"
              type="button"
              onClick={() => image.current?.click()}
            >
              <i18n.Translate>Add</i18n.Translate>
            </button>
          )}
          {value && (
            <button
              type="button"
              class="button"
              onClick={() => {
                if (image.current) {
                  image.current.value = "";
                }
                onChange(undefined!);
                setSizeError(undefined);
              }}
            >
              <i18n.Translate>Remove</i18n.Translate>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

async function toDataSrc(imageFile: File): Promise<string> {
  return imageFile.arrayBuffer().then((b) => {
    const b64 = window.btoa(
      new Uint8Array(b).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        "",
      ),
    );
    return `data:${imageFile.type};base64,${b64}`;
  });
}

const IMAGE_MAX_WIDTH = 800;
const IMAGE_MAX_HEIGHT = 800;
function normalizeImagesize(imageFile: File, onComplete: (d: string) => void) {
  toDataSrc(imageFile).then((imageSource) => {
    const img = new Image();
    img.src = imageSource;
    img.onload = function () {
      const canvas = document.createElement("canvas") as HTMLCanvasElement;
      const ctx = canvas.getContext("2d")!;

      let width = img.width;
      let height = img.height;
      if (width > height) {
        if (width > IMAGE_MAX_WIDTH) {
          height *= IMAGE_MAX_WIDTH / width;
          width = IMAGE_MAX_WIDTH;
        }
      } else {
        if (height > IMAGE_MAX_HEIGHT) {
          width *= IMAGE_MAX_HEIGHT / height;
          height = IMAGE_MAX_HEIGHT;
        }
      }
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      onComplete(canvas.toDataURL());
    };
  });
}
